/**
 * 見積一覧画面 (admin)。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §6, §8
 *
 * UI: イベント管理画面と統一したカード形式リスト (アイコン + タイトル + メタ + 右端アクション)
 */
import type { QuoteStatus } from '@goenhub/shared';
import { Copy, ExternalLink, FileText } from 'lucide-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useDeals } from '@/hooks/useDeals';
import { useQuotes } from '@/hooks/useQuotes';
import { paths } from '@/routes/paths';

const STATUS_LABEL: Record<QuoteStatus, string> = {
  draft: '下書き',
  sent: '送信済',
  expired: '期限切れ',
  won: '成約',
  lost: '失注',
};

const STATUS_VARIANT: Record<
  QuoteStatus,
  'draft' | 'sent' | 'danger' | 'approved'
> = {
  draft: 'draft',
  sent: 'sent',
  expired: 'danger',
  won: 'approved',
  lost: 'danger',
};

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}

function publicUrlFor(token: string): string {
  return `${window.location.origin}/quote/${token}`;
}

export function QuotesListPage() {
  const navigate = useNavigate();
  const { data: quotes, isLoading } = useQuotes();
  const { data: deals } = useDeals();

  const dealTitleMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of deals ?? []) m.set(d.id, d.title);
    return m;
  }, [deals]);

  return (
    <Screen
      title="見積管理"
      description="作成済み見積書の一覧。公開URLのコピー、再送信、詳細確認ができます。"
    >
      {isLoading ? (
        <Spinner />
      ) : !quotes || quotes.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="見積書はまだありません"
          description="商談詳細画面から「見積を作成」ボタンで作成できます。"
        />
      ) : (
        <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
          {quotes.map((q) => (
            <div
              key={q.id}
              className="flex items-center gap-4 p-4 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors"
            >
              <FileText className="size-4 text-[var(--text-muted)] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-[13px] truncate">
                    {q.title || `見積 ${q.id.slice(0, 6)}`}
                  </span>
                  <Badge variant={STATUS_VARIANT[q.status]}>
                    {STATUS_LABEL[q.status]}
                  </Badge>
                </div>
                <div className="text-xs text-[var(--text-muted)] flex items-center gap-3 flex-wrap">
                  <span>{dealTitleMap.get(q.dealId) ?? q.dealId}</span>
                  <span className="tabular-nums">
                    ¥{q.totalIncludingTax.toLocaleString()}
                  </span>
                  <span>発行: {fmtDate(q.issuedAt)}</span>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => {
                  navigator.clipboard.writeText(publicUrlFor(q.publicToken));
                  toast.success('公開URLをコピーしました');
                }}
                title="公開URLをコピー"
              >
                <Copy />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() =>
                  navigate(
                    paths.quoteDetail
                      .replace(':dealId', q.dealId)
                      .replace(':quoteId', q.id),
                  )
                }
                title="詳細を表示"
              >
                <ExternalLink />
              </Button>
            </div>
          ))}
        </div>
      )}
    </Screen>
  );
}
